'use client';

import React, { useEffect, useTransition } from 'react';
import { useRouter } from 'next/navigation';

interface TransitionLinkProps {
  href: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
}

// Global overlay element for page transitions
let overlayEl: HTMLDivElement | null = null;

function getOverlay(): HTMLDivElement {
  if (!overlayEl) {
    overlayEl = document.createElement('div');
    overlayEl.style.cssText = `
      position: fixed;
      inset: 0;
      background: #000;
      z-index: 99999;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      will-change: opacity;
    `;
    document.body.appendChild(overlayEl);
  }
  return overlayEl;
}

export default function TransitionLink({ href, children, style, className }: TransitionLinkProps) {
  const router = useRouter();
  const [, startTransition] = useTransition();

  function handleClick(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    const overlay = getOverlay();
    overlay.style.transition = 'opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1)';
    overlay.style.pointerEvents = 'all';
    overlay.style.opacity = '1';
    setTimeout(() => {
      startTransition(() => {
        router.push(href);
      });
      setTimeout(() => {
        overlay.style.transition = 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        overlay.style.opacity = '0';
        overlay.style.pointerEvents = 'none';
      }, 200);
    }, 260);
  }

  return (
    <a href={href} onClick={handleClick} style={style} className={className}>
      {children}
    </a>
  );
}

// Hook to trigger fade-in on page mount
export function usePageFadeIn() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const overlay = getOverlay();
    overlay.style.transition = 'none';
    overlay.style.opacity = '1';
    overlay.style.pointerEvents = 'none';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        overlay.style.transition = 'opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1)';
        overlay.style.opacity = '0';
      });
    });
  }, []);
}
